import { useUserStore } from '../../../store/useUserStore';
import { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import toast from 'react-hot-toast';
import moment from 'moment';
import {
  useEventDetailQuery,
  useEventCommentsQuery,
  useCreateEventCommentMutation,
} from '../../../queries';
import { COMPLAINT_TARGET_TYPE } from '../../../data/define';
import useInfiniteScroll from '../../../hook/useInfiniteScroll';
import FullHeightContainer from '../../common/FullHeightContainer';
import TopHeaderWithPrev from '../../common/TopHeaderWithPrev';
import Divider from '../../common/Divider';
import CommentCard from '../CommentCard';
import BottomFixedTextField from '../../common/BottomFixedTextField';
import OptionDialog from './OptionDialog';
import ComplaintDialog from '../ComplaintDialog';
import { useTranslation } from '../../../hook/useTranslation';

const DateSort = {
  Registration: 1, // 'ASC'
  Latest: 2, // 'DESC'
};

const LIMIT = 10;

interface PostDetailProps {
  drawerOpen: boolean;
  onClickPrev: () => void;
  postId?: number | null;
}

export default function PostDetail({ drawerOpen, onClickPrev, postId = null }: PostDetailProps) {
  const user = useUserStore();
  const { t } = useTranslation();
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [optionOpenId, setOptionOpenId] = useState<number | null>(null);
  const [complaintOpenId, setComplaintOpenId] = useState<number | null>(null);
  const [dateSorting, setDateSorting] = useState(DateSort.Latest);
  const [inputValue, setInputValue] = useState('');
  const [comments, setComments] = useState<any[]>([]);

  // 1. 이벤트 상세 조회 Query
  const { data: detail } = useEventDetailQuery(postId || 0, {
    enabled: drawerOpen && !!postId,
  });

  // 2. 이벤트 댓글 조회 Query
  const commentsParams = useMemo(
    () => ({
      eventId: postId || 0,
      limit: LIMIT,
      page,
      order: dateSorting === DateSort.Registration ? 'ASC' : 'DESC',
      sortBy: dateSorting === DateSort.Registration ? 'createdAt' : 'updatedAt',
    }),
    [postId, page, dateSorting]
  );

  const {
    data: commentsData,
    isLoading,
    refetch: refetchComments,
  } = useEventCommentsQuery(commentsParams, { enabled: drawerOpen && !!postId });

  useEffect(() => {
    if (commentsData) {
      if (page === 1) {
        setComments(commentsData.items);
      } else {
        setComments((prev) => [...prev, ...commentsData.items]);
      }
      setTotalCount(commentsData.total);
    }
  }, [commentsData, page]);

  const refreshComments = useCallback(() => {
    setPage(1);
    setComments([]);
    setHasMore(true);
    refetchComments();
  }, [refetchComments]);

  // 등록순/최신순 변경 시 refresh
  const dateSortingRef = useRef(DateSort.Latest);
  useEffect(() => {
    if (dateSortingRef.current === dateSorting) return;
    refreshComments();
    dateSortingRef.current = dateSorting;
  }, [dateSorting, refreshComments]);

  // 3. 댓글 작성 Mutation
  const { mutate: createComment } = useCreateEventCommentMutation(postId || 0);

  // infinite scroll
  const { lastItemRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore: () => {
      if (totalCount <= page * LIMIT) {
        setHasMore(false);
        return;
      }
      setPage((prev) => prev + 1);
    },
  });

  const handleCommentSubmit = useCallback(async () => {
    if (!inputValue.trim()) return;
    createComment(
      { content: inputValue },
      {
        onSuccess: () => {
          setInputValue('');
          refreshComments();
        },
        onError: (err) => {
          console.error('Failed to create comment', err);
          toast.error(t('community.toast_comment_create_fail'));
        },
      }
    );
  }, [inputValue, createComment, refreshComments, t]);

  // 옵션버튼 클릭
  const handleOptionClick = useCallback((targetId: number) => {
    setOptionOpenId(targetId);
  }, []);

  const handleComplaintReport = (targetId: number) => {
    setComplaintOpenId(targetId);
    setOptionOpenId(null);
  };

  return (
    <FullHeightContainer>
      <TopHeaderWithPrev title={t('community.event.title')} onClickPrev={onClickPrev} />
      <div className="pageInner">
        <div className="w-full flex flex-col items-start justify-between py-4">
          <div className="text-left mb-3">
            <div className="font-[700] text-[16px]">{detail?.title}</div>
          </div>
          <div className="w-full flex justify-between items-center">
            <div className="text-[12px] font-[400] text-[#969696]">
              {moment(detail?.createdAt).format('YYYY.MM.DD')}
            </div>
          </div>
          <img
            src={detail?.images?.[0]?.url}
            alt="event-image"
            className="w-full min-h-[180px] mt-8 object-cover rounded-[8px] mb-2"
          />
          <div
            className="w-full text-[14px] font-[400] mt-5 text-[#1C1C1C]"
            dangerouslySetInnerHTML={{ __html: detail?.content || '' }}
          />
        </div>
        <div className="my-[24px] w-full">
          <Divider />
        </div>
        <div className="w-full flex justify-between">
          <div className="flex justify-start items-center text-[16px] font-[700] gap-1">
            {t('community.comment_count', { count: totalCount || 0 })}
          </div>
          <div className="flex justify-end items-center text-[14px] font-[700] gap-2">
            <button
              type="button"
              style={{
                color:
                  dateSorting === DateSort.Registration && comments.length !== 0
                    ? '#363636'
                    : '#969696',
              }}
              onClick={() => setDateSorting(DateSort.Registration)}
              disabled={comments.length === 0}
            >
              {t('common.sort_registration')}
            </button>
            <button
              type="button"
              style={{
                color:
                  dateSorting === DateSort.Latest && comments.length !== 0 ? '#363636' : '#969696',
              }}
              onClick={() => setDateSorting(DateSort.Latest)}
              disabled={comments.length === 0}
            >
              {t('common.sort_latest')}
            </button>
          </div>
        </div>
        <div className="w-full">
          {comments.length >= 1 ? (
            comments?.map((comment, idx) => (
              <CommentCard
                key={comment.id}
                comment={comment}
                optionClick={handleOptionClick}
                ref={idx === comments.length - 1 ? lastItemRef : undefined}
              />
            ))
          ) : (
            <div className="text-center text-[#767676] text-[14px] mt-20">
              {t('community.no_comments').split('\n').map((line, i) => (
                <span key={i}>
                  {line}
                  {i === 0 && <br />}
                </span>
              ))}
            </div>
          )}
          <OptionDialog
            open={optionOpenId !== null}
            onClose={() => setOptionOpenId(null)}
            isUsersPost={
              comments?.find((item) => item.id === optionOpenId)?.author?.id === user?.id
            }
            selectedId={optionOpenId as number}
            reload={refreshComments}
            onComplaintClick={handleComplaintReport}
          />
          {complaintOpenId !== null && (
            <ComplaintDialog
              open={complaintOpenId !== null}
              onClose={() => setComplaintOpenId(null)}
              targetType={COMPLAINT_TARGET_TYPE.EVENT_ANSWER}
            />
          )}
        </div>
      </div>
      <BottomFixedTextField
        inputValue={inputValue}
        setInputValue={setInputValue}
        onSubmit={handleCommentSubmit}
      />
    </FullHeightContainer>
  );
}
